import { IntlShape } from 'react-intl'
import { z } from 'zod'
import { productValidationMessages } from '../../../../messages'

export const getProductValidationSchema = (formatMessage: IntlShape['formatMessage']) => {
  const positiveNumber = z
    .union([
      z
        .string()
        .trim()
        .transform((val) => (val === '' ? undefined : parseFloat(val))),
      z.number(),
    ])
    .nullable()
    .transform((val) => (val === null || isNaN(val as number) ? undefined : val))
    .refine((val) => val === undefined || val >= 0, {
      message: formatMessage(productValidationMessages.positiveNumber),
    })

  const number = z
    .union([
      z
        .string()
        .trim()
        .transform((val) => (val === '' ? undefined : parseFloat(val))),
      z.number(),
    ])
    .nullable()
    .transform((val) => (val === null || isNaN(val as number) ? undefined : val))

  const basePriceSchema = z.object({
    id: z.number().optional(),
    productId: z.number().optional(),
    currencyId: z.number({
      required_error: formatMessage(productValidationMessages.currencyRequired),
    }),
    documents: z.array(
      z.object({
        id: z.number().optional(),
        name: z.string().optional(),
        expiryDate: z
          .union([z.string(), z.date()])
          .refine((val) => val !== null && val !== '', {
            message: formatMessage(productValidationMessages.documentExpiryRequired),
          })
          .transform((val) => (val === '' ? new Date().toISOString() : val)),
        documentTypeId: z.number().optional(),
        fileId: z.string().optional(),
      })
    ),
    price: z
      .union([z.string(), z.number()])
      .transform((val) => {
        if (typeof val === 'string') {
          const parsed = parseFloat(val)
          return isNaN(parsed) ? 0 : parsed
        }
        return val
      })
      .refine((val) => val >= 0.01, {
        message: formatMessage(productValidationMessages.priceMinimum),
      })
      .refine((val) => val <= 9999999999.99, {
        message: formatMessage(productValidationMessages.priceMaximum),
      }),
    name: z.string().optional(),
    priceValidFrom: z
      .union([z.string(), z.date()])
      .refine((val) => val !== null && val !== '', {
        message: 'Valid From date is required',
      })
      .transform((val) => {
        if (val === null || val === '') {
          return new Date().toString()
        }
        return val
      }),
    priceValidTo: z
      .union([z.string(), z.date(), z.null()])
      .nullable()
      .transform((val) => {
        if (val === '' || val === '∞') {
          return null
        }
        return val
      }),
    validFrom: z
      .union([z.string(), z.date(), z.null()])
      .transform((val) => {
        if (val === null || val === '') {
          return new Date().toISOString()
        }
        return val
      })
      .optional(),
    validTo: z
      .union([z.string(), z.date(), z.null()])
      .nullable()
      .transform((val) => {
        if (val === '' || val === '∞') {
          return null
        }
        return val
      })
      .optional(),
  })

  const physicalPropertiesSchema = z
    .object({
      id: z.number().optional(),
      weightMeasureUnitId: z.number().optional().nullable(),
      volumeMeasureUnitId: z.number().optional().nullable(),
      lengthMeasureUnitId: z.number().optional().nullable(),
      densityMeasureUnitId: z.number().optional().nullable(),
      isRectangular: z.boolean().optional(),
      useDimensions: z.boolean().optional(),
      netWeight: positiveNumber.optional(),
      grossWeight: positiveNumber.optional(),
      volume: positiveNumber.optional(),
      height: positiveNumber.optional(),
      length: positiveNumber.optional(),
      width: positiveNumber.optional(),
      density: positiveNumber.optional(),
      maxLoadingHeight: positiveNumber.optional(),
      updatedByUserId: z.number().optional(),
      validFrom: z.string().optional(),
      validTo: z.string().optional(),
    })
    .optional()
    .superRefine((data, ctx) => {
      const providedPP = {
        useDimensions: false,
      }

      if (JSON.stringify(data) === JSON.stringify(providedPP)) {
        return
      }

      if (!data?.netWeight) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage(productValidationMessages.netWeightRequired),
          path: ['netWeight'],
        })
      }
      if (!data?.grossWeight) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage(productValidationMessages.grossWeightRequired),
          path: ['grossWeight'],
        })
      }
      if (
        data &&
        data.density &&
        (!data.densityMeasureUnitId || data.densityMeasureUnitId === -1)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage(productValidationMessages.densityUnitsRequired),
          path: ['density'],
        })
      }
      if (
        data &&
        data.netWeight &&
        (!data.weightMeasureUnitId || data.weightMeasureUnitId === -1)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage(productValidationMessages.weightUnitRequired),
          path: ['netWeight'],
        })
      }
      if (data && data.volume && (!data.volumeMeasureUnitId || data.volumeMeasureUnitId === -1)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage(productValidationMessages.volumeUnitRequired),
          path: ['volume'],
        })
      }
      if (
        data &&
        (data.length || data.width || data.height) &&
        (!data.lengthMeasureUnitId || data.lengthMeasureUnitId === -1)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage(productValidationMessages.lengthUnitRequired),
          path: ['length'],
        })
      }
      if (data) {
        if (data.isRectangular) {
          if (!data.length) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: formatMessage(productValidationMessages.lengthRequired),
              path: ['length'],
            })
          }
          if (!data.width) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: formatMessage(productValidationMessages.widthRequired),
              path: ['width'],
            })
          }
          if (!data.height) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: formatMessage(productValidationMessages.heightRequired),
              path: ['height'],
            })
          }
        } else if (data.useDimensions) {
          if (!data.length) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: formatMessage(productValidationMessages.diameterRequired),
              path: ['length'],
            })
          }
          if (!data.height) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: formatMessage(productValidationMessages.heightRequired),
              path: ['height'],
            })
          }
        }
        if (data.useDimensions === false) {
          if (!data.volume) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: formatMessage(productValidationMessages.volumeRequired),
              path: ['volume'],
            })
          }
          if (!data.density) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: formatMessage(productValidationMessages.densityRequired),
              path: ['density'],
            })
          }
        }
      }
    })

  // Helper to normalize null to undefined for optional ID fields
  const optionalId = z
    .number()
    .nullable()
    .optional()
    .transform((val) => (val == null ? undefined : val))

  const temperatureSettingSchemaApi = z
    .object({
      id: z.number().optional(),
      temperatureRangeId: optionalId,
      temperatureUnitId: optionalId,
      // Use number helper for all numeric fields to transform '' to undefined
      setPoint: number.optional(),
      upperThresholdWarning: number.optional(),
      upperThresholdCritical: number.optional(),
      lowerThresholdWarning: number.optional(),
      lowerThresholdCritical: number.optional(),
      updatedByUserId: z.number().optional(),
      validFrom: z.string().optional(),
      validTo: z.string().optional(),
    })
    .optional()

  return z
    .object({
      id: z.number().optional(),
      enabled: z.boolean().default(true),
      name: z
        .string()
        .min(1, { message: formatMessage(productValidationMessages.nameRequired) })
        .max(250, { message: formatMessage(productValidationMessages.nameMaxLength) }),

      customerId: z.number({
        required_error: formatMessage(productValidationMessages.customerRequired),
        invalid_type_error: formatMessage(productValidationMessages.customerIdType),
      }),

      sku: z
        .string()
        .min(1, { message: formatMessage(productValidationMessages.skuRequired) })
        .max(50, { message: formatMessage(productValidationMessages.skuMaxLength) })
        .regex(/^[a-zA-Z0-9\-_.#,]*$/, {
          message: formatMessage(productValidationMessages.skuFormat),
        }),

      productCategoryId: z
        .number({
          required_error: formatMessage(productValidationMessages.productCategoryRequired),
          invalid_type_error: formatMessage(productValidationMessages.productCategoryIdType),
        })
        .min(1, {
          message: formatMessage(productValidationMessages.productCategoryRequired),
        }),

      countryOfOriginId: z
        .number({
          required_error: formatMessage(productValidationMessages.countryOfOriginRequired),
          invalid_type_error: formatMessage(productValidationMessages.countryOfOriginIdType),
        })
        .min(1, {
          message: formatMessage(productValidationMessages.countryOfOriginRequired),
        }),

      barcode: z
        .string()
        .max(50, { message: formatMessage(productValidationMessages.barcodeMaxLength) })
        .regex(/^[a-zA-Z0-9]*$/, {
          message: formatMessage(productValidationMessages.barcodeFormat),
        })
        .optional()
        .nullable()
        .transform((val) => (val === '' || val === null ? undefined : val)),

      prices: z
        .array(basePriceSchema)
        .optional()
        .default([])
        .superRefine((prices, ctx) => {
          if (!prices || prices.length === 0) {
            return
          }

          const hasNoEndDate = prices.some((price) => {
            return !price.priceValidTo || price.priceValidTo === '∞'
          })
          if (!hasNoEndDate) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'At least one price entry must have no end date',
              path: [],
            })
          }

          const sortedPrices = [...prices].sort((a, b) => {
            const dateAObj = a.priceValidFrom ? new Date(a.priceValidFrom) : null
            const dateBObj = b.priceValidFrom ? new Date(b.priceValidFrom) : null

            const dateA = dateAObj
              ? new Date(dateAObj.getFullYear(), dateAObj.getMonth(), dateAObj.getDate()).getTime()
              : 0

            const dateB = dateBObj
              ? new Date(dateBObj.getFullYear(), dateBObj.getMonth(), dateBObj.getDate()).getTime()
              : 0

            return dateA - dateB
          })

          for (let i = 0; i < sortedPrices.length - 1; i++) {
            const currentPrice = sortedPrices[i]
            const nextPrice = sortedPrices[i + 1]
            if (!currentPrice.priceValidTo) continue

            const currentEndDateObj = currentPrice.priceValidTo
              ? new Date(currentPrice.priceValidTo)
              : null
            const nextStartDateObj = nextPrice.priceValidFrom
              ? new Date(nextPrice.priceValidFrom)
              : null

            if (currentEndDateObj && nextStartDateObj) {
              const currentEndDateOnly = new Date(
                currentEndDateObj.getFullYear(),
                currentEndDateObj.getMonth(),
                currentEndDateObj.getDate()
              ).getTime()

              const nextStartDateOnly = new Date(
                nextStartDateObj.getFullYear(),
                nextStartDateObj.getMonth(),
                nextStartDateObj.getDate()
              ).getTime()

              if (currentEndDateOnly < nextStartDateOnly) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: 'There cannot be gaps between price date ranges',
                  path: [],
                })
                break
              }
            }
          }

          const currenciesWithNoEndDate = new Map<number, number>()

          prices.forEach((price) => {
            if (!price.priceValidTo || price.priceValidTo === null) {
              const currentCount = currenciesWithNoEndDate.get(price.currencyId) || 0
              currenciesWithNoEndDate.set(price.currencyId, currentCount + 1)
            }
          })

          const currencyWithMultipleNoEndDates = Array.from(currenciesWithNoEndDate.entries()).find(
            ([, count]) => count > 1
          )

          if (currencyWithMultipleNoEndDates) {
            const currencyId = currencyWithMultipleNoEndDates[0]
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Currency ID ${currencyId} has multiple prices with no end date. Only one price per currency can have no end date.`,
              path: [],
            })
          }

          const pricesByCurrency = new Map<number, typeof prices>()
          prices.forEach((price) => {
            const arr = pricesByCurrency.get(price.currencyId)
            if (arr) {
              arr.push(price)
            } else {
              pricesByCurrency.set(price.currencyId, [price])
            }
          })

          pricesByCurrency.forEach((currencyPrices, currencyId) => {
            const sorted = [...currencyPrices].sort((a, b) => {
              const aStartObj = a.priceValidFrom ? new Date(a.priceValidFrom) : null
              const bStartObj = b.priceValidFrom ? new Date(b.priceValidFrom) : null
              const aStart = aStartObj
                ? new Date(
                    aStartObj.getFullYear(),
                    aStartObj.getMonth(),
                    aStartObj.getDate()
                  ).getTime()
                : 0
              const bStart = bStartObj
                ? new Date(
                    bStartObj.getFullYear(),
                    bStartObj.getMonth(),
                    bStartObj.getDate()
                  ).getTime()
                : 0
              return aStart - bStart
            })
            for (let i = 0; i < sorted.length; i++) {
              const a = sorted[i]
              const aStartObj = a.priceValidFrom ? new Date(a.priceValidFrom) : null
              const aEndObj =
                a.priceValidTo && a.priceValidTo !== '∞' ? new Date(a.priceValidTo) : null
              const aStart = aStartObj
                ? new Date(
                    aStartObj.getFullYear(),
                    aStartObj.getMonth(),
                    aStartObj.getDate()
                  ).getTime()
                : 0
              const aEnd = aEndObj
                ? new Date(aEndObj.getFullYear(), aEndObj.getMonth(), aEndObj.getDate()).getTime()
                : Infinity
              for (let j = i + 1; j < sorted.length; j++) {
                const b = sorted[j]
                const bStartObj = b.priceValidFrom ? new Date(b.priceValidFrom) : null
                const bEndObj =
                  b.priceValidTo && b.priceValidTo !== '∞' ? new Date(b.priceValidTo) : null
                const bStart = bStartObj
                  ? new Date(
                      bStartObj.getFullYear(),
                      bStartObj.getMonth(),
                      bStartObj.getDate()
                    ).getTime()
                  : 0
                const bEnd = bEndObj
                  ? new Date(bEndObj.getFullYear(), bEndObj.getMonth(), bEndObj.getDate()).getTime()
                  : Infinity
                if (aStart < bEnd && bStart < aEnd) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Price periods overlap for currency ID ${currencyId}`,
                    path: [],
                  })
                  return
                }
              }
            }
          })

          const nowDate = new Date()
          const nowDateOnly = new Date(
            nowDate.getFullYear(),
            nowDate.getMonth(),
            nowDate.getDate()
          ).getTime()

          const hasActivePrice = prices.some((price) => {
            const startDateObj = price.priceValidFrom ? new Date(price.priceValidFrom) : null

            const startDateOnly = startDateObj
              ? new Date(
                  startDateObj.getFullYear(),
                  startDateObj.getMonth(),
                  startDateObj.getDate()
                ).getTime()
              : 0

            const endDateObj = price.priceValidTo ? new Date(price.priceValidTo) : null
            const endDateValid =
              !price.priceValidTo ||
              (endDateObj
                ? new Date(
                    endDateObj.getFullYear(),
                    endDateObj.getMonth(),
                    endDateObj.getDate()
                  ).getTime() >= nowDateOnly
                : true)

            return startDateOnly <= nowDateOnly && endDateValid
          })

          if (!hasActivePrice) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'There must be an active price for the current date',
              path: [],
            })
          }
        }),

      inventoryUnitId: z
        .number({
          required_error: formatMessage(productValidationMessages.inventoryUnitRequired),
          invalid_type_error: formatMessage(productValidationMessages.inventoryUnitIdType),
        })
        .min(1, {
          message: formatMessage(productValidationMessages.inventoryUnitRequired),
        }),
      physicalPropertiesId: z.number().optional(),
      physicalProperties: physicalPropertiesSchema,
      temperatureSettingId: optionalId,
      temperatureSetting: temperatureSettingSchemaApi,
      validFrom: z.string().optional(),
      validTo: z.string().optional(),
      documents: z.array(
        z.object({
          id: z.number().optional(),
          name: z.string().min(1, {
            message: formatMessage(productValidationMessages.documentNameRequired),
          }),
          expiryDate: z
            .union([z.string(), z.date()])
            .refine((val) => val !== null && val !== '', {
              message: formatMessage(productValidationMessages.documentExpiryRequired),
            })
            .transform((val) => (val === '' ? new Date().toISOString() : val)),
          documentTypeId: z.number().optional(),
          fileId: z.string().optional(),
        })
      ),
    })
    .superRefine((product, ctx) => {
      // Temperature settings validation - only validate when temperatureSetting exists
      const ts = product.temperatureSetting
      if (!ts) return

      // Required fields validation when temperature range is set
      if (ts.temperatureRangeId !== undefined) {
        // Require set point when temperature range is set
        if (ts.setPoint === undefined || ts.setPoint === null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: formatMessage(productValidationMessages.setPointRequired),
            path: ['temperatureSetting', 'setPoint'],
          })
        }

        // Require temperature unit when temperature range is set
        if (!ts.temperatureUnitId) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: formatMessage(productValidationMessages.temperatureUnitRequired),
            path: ['temperatureSetting', 'setPoint'],
          })
        }

        // Require lower critical threshold when temperature range is set
        if (ts.lowerThresholdCritical === undefined || ts.lowerThresholdCritical === null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: formatMessage(productValidationMessages.lowerThresholdCriticalRequired),
            path: ['temperatureSetting', 'lowerThresholdCritical'],
          })
        }

        // Require upper critical threshold when temperature range is set
        if (ts.upperThresholdCritical === undefined || ts.upperThresholdCritical === null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: formatMessage(productValidationMessages.upperThresholdCriticalRequired),
            path: ['temperatureSetting', 'upperThresholdCritical'],
          })
        }

        // Set Point vs Critical thresholds validation
        // Set Point cannot be less than Lower Critical
        if (
          ts.setPoint !== undefined &&
          ts.lowerThresholdCritical !== undefined &&
          ts.setPoint < ts.lowerThresholdCritical
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: formatMessage(productValidationMessages.setPointLowerCriticalComparison),
            path: ['temperatureSetting', 'setPoint'],
          })
        }

        // Set Point cannot be more than Upper Critical
        if (
          ts.setPoint !== undefined &&
          ts.upperThresholdCritical !== undefined &&
          ts.setPoint > ts.upperThresholdCritical
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: formatMessage(productValidationMessages.setPointUpperCriticalComparison),
            path: ['temperatureSetting', 'setPoint'],
          })
        }

        // Upper Critical cannot be lower than Set Point
        if (
          ts.upperThresholdCritical !== undefined &&
          ts.setPoint !== undefined &&
          ts.upperThresholdCritical < ts.setPoint
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: formatMessage(productValidationMessages.upperCriticalSetPointComparison),
            path: ['temperatureSetting', 'upperThresholdCritical'],
          })
        }

        // Lower Critical cannot be more than Set Point
        if (
          ts.lowerThresholdCritical !== undefined &&
          ts.setPoint !== undefined &&
          ts.lowerThresholdCritical > ts.setPoint
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: formatMessage(productValidationMessages.lowerCriticalSetPointComparison),
            path: ['temperatureSetting', 'lowerThresholdCritical'],
          })
        }

        // Warning thresholds validation
        // Lower Warning cannot be less than Lower Critical
        if (
          ts.lowerThresholdWarning !== undefined &&
          ts.lowerThresholdWarning !== null &&
          ts.lowerThresholdCritical !== undefined &&
          ts.lowerThresholdCritical !== null &&
          ts.lowerThresholdWarning < ts.lowerThresholdCritical
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: formatMessage(productValidationMessages.lowerWarningLowerCriticalComparison),
            path: ['temperatureSetting', 'lowerThresholdWarning'],
          })
        }

        // Upper Warning cannot be more than Upper Critical
        if (
          ts.upperThresholdWarning !== undefined &&
          ts.upperThresholdWarning !== null &&
          ts.upperThresholdCritical !== undefined &&
          ts.upperThresholdCritical !== null &&
          ts.upperThresholdWarning > ts.upperThresholdCritical
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: formatMessage(productValidationMessages.upperWarningUpperCriticalComparison),
            path: ['temperatureSetting', 'upperThresholdWarning'],
          })
        }
      }
    })
}

export type ProductSchemaType = z.infer<ReturnType<typeof getProductValidationSchema>>
